import React, { useEffect, useState } from 'react';
import { customerApi, useCustomerToken } from './customerApi';
import { Navigate, useNavigate } from 'react-router-dom';
import { Banknote, Lock, Smartphone } from 'lucide-react';
import { cn } from '../utils/cn';
import { getApiErrorMessage } from '../services/api';
import { clearCart, getDetails, rememberOrder, rupees, saveDetails, shopApi, type Details } from './shopApi';
import { useTotals } from './CartPage';

function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  const name = d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  return diff === 0 ? `Today · ${name}` : diff === 1 ? `Tomorrow · ${name}` : name;
}

const inputCls = 'w-full rounded-lg border border-ink-300/50 bg-white px-3 py-2.5 text-base focus:border-brand-green-600 focus:outline-none focus:ring-2 focus:ring-brand-green-100';

function Field({ label, error, children, hint }: { label: string; error?: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium text-ink-700">{label}</span>
      {children}
      {hint && !error && <span className="text-xs text-ink-400">{hint}</span>}
      {error && <span className="text-xs font-medium text-critical-600">{error}</span>}
    </label>
  );
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { front, lines, subtotal, fee, total, short } = useTotals();
  const [d, setD] = useState<Details>(getDetails);
  const [date, setDate] = useState('');
  const [slot, setSlot] = useState('');
  const [method, setMethod] = useState<'UPI' | 'Cash'>('UPI');
  const [notes, setNotes] = useState('');
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [hp, setHp] = useState('');
  // A signed-in subscription customer: their address from the farm's records fills empty boxes.
  const signedIn = useCustomerToken();
  useEffect(() => {
    if (!signedIn) return;
    customerApi.address().then((a) => setD((cur) => ({
      ...cur,
      customer_name: cur.customer_name || a.customer_name, mobile: cur.mobile || a.mobile, flat: cur.flat || a.flat,
      building: cur.building || a.building, area: cur.area || a.area, pincode: cur.pincode || a.pincode,
    }))).catch(() => undefined);
  }, [signedIn]);

  if (!front) return null;
  if (lines.length === 0) return <Navigate to="/shop/cart" replace />;
  const day = date || front.dates[0] || '';
  const when = slot || front.slots[0] || 'Morning';
  const pay = front.upi ? method : 'Cash';
  const set = (k: keyof Details) => (e: React.ChangeEvent<HTMLInputElement>) => setD((cur) => ({ ...cur, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (d.customer_name.trim().length < 2) next.customer_name = 'Please enter your name.';
    if (!/^[6-9]\d{9}$/.test(d.mobile.replace(/\D/g, '').replace(/^(91|0)(?=\d{10}$)/, ''))) next.mobile = 'Enter a 10-digit mobile number.';
    if (!d.flat.trim()) next.flat = 'Flat / house number is needed.';
    if (d.area.trim().length < 2) next.area = 'Area / street is needed.';
    if (!/^\d{6}$/.test(d.pincode)) next.pincode = 'Enter the 6-digit PIN code.';
    else if (front.pincodes.length && !front.pincodes.includes(d.pincode)) next.pincode = `We don't deliver to ${d.pincode} yet.${front.delivery_area_note ? ` ${front.delivery_area_note}` : ''}`;
    if (!agree) next.agree = 'Please tick to agree.';
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    setError(null);
    try {
      saveDetails(d);
      const order = await shopApi.place({
        ...d, items: lines.map((l) => ({ product_id: l.product.id, quantity: l.qty })),
        delivery_date: day, delivery_slot: when, payment_method: pay, notes, agree, website: hp,
      });
      rememberOrder(order);
      clearCart();
      navigate(`/shop/order/${order.ref}`, { replace: true });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not place the order. Please try again.'));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <h1 className="text-xl font-bold text-brand-green-900">Delivery details</h1>

      <section className="flex flex-col gap-3 rounded-xl bg-white p-4">
        <Field label="Your name" error={errors.customer_name}><input className={inputCls} autoComplete="name" value={d.customer_name} onChange={set('customer_name')} /></Field>
        <Field label="Mobile number" error={errors.mobile} hint="To track your order and for the delivery person to call.">
          <input className={inputCls} inputMode="numeric" autoComplete="tel" placeholder="98XXXXXXXX" value={d.mobile} onChange={set('mobile')} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Flat / house no." error={errors.flat}><input className={inputCls} placeholder="e.g. 302" value={d.flat} onChange={set('flat')} /></Field>
          <Field label="Apartment / building"><input className={inputCls} value={d.building} onChange={set('building')} /></Field>
        </div>
        <Field label="Area / street" error={errors.area}><input className={inputCls} autoComplete="address-line1" value={d.area} onChange={set('area')} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Landmark"><input className={inputCls} value={d.landmark} onChange={set('landmark')} /></Field>
          <Field label="PIN code" error={errors.pincode}><input className={inputCls} inputMode="numeric" maxLength={6} autoComplete="postal-code" value={d.pincode} onChange={set('pincode')} /></Field>
        </div>
        {front.delivery_area_note && <p className="text-xs text-ink-500">{front.delivery_area_note}</p>}
      </section>

      <section className="flex flex-col gap-3 rounded-xl bg-white p-4">
        <p className="text-sm font-semibold">When should we deliver?</p>
        <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1">
          {front.dates.map((dt) => (
            <button key={dt} type="button" onClick={() => setDate(dt)}
              className={cn('shrink-0 rounded-lg border px-3 py-2 text-sm', day === dt ? 'border-brand-green-700 bg-brand-green-50 font-semibold text-brand-green-800' : 'border-ink-300/40')}>
              {dayLabel(dt)}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {front.slots.map((s) => (
            <button key={s} type="button" onClick={() => setSlot(s)}
              className={cn('flex-1 rounded-lg border py-2 text-sm', when === s ? 'border-brand-green-700 bg-brand-green-50 font-semibold text-brand-green-800' : 'border-ink-300/40')}>
              {s === 'Morning' ? 'Morning (AM)' : 'Evening (PM)'}
            </button>
          ))}
        </div>
        <Field label="Note for delivery (optional)">
          <input className={inputCls} maxLength={500} placeholder="e.g. Leave at the door, ring bell twice" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </section>

      <section className="flex flex-col gap-2 rounded-xl bg-white p-4">
        <p className="text-sm font-semibold">How will you pay?</p>
        {front.upi && (
          <button type="button" onClick={() => setMethod('UPI')} className={cn('flex items-center gap-3 rounded-lg border p-3 text-left', pay === 'UPI' ? 'border-brand-green-700 bg-brand-green-50' : 'border-ink-300/40')}>
            <Smartphone className="h-5 w-5 text-brand-green-700" />
            <span><span className="block text-sm font-semibold">UPI — PhonePe, Google Pay, Paytm, BHIM</span><span className="block text-xs text-ink-500">Pay now. The exact amount opens in your UPI app.</span></span>
          </button>
        )}
        {front.cash_on_delivery && (
          <button type="button" onClick={() => setMethod('Cash')} className={cn('flex items-center gap-3 rounded-lg border p-3 text-left', pay === 'Cash' ? 'border-brand-green-700 bg-brand-green-50' : 'border-ink-300/40')}>
            <Banknote className="h-5 w-5 text-brand-green-700" />
            <span><span className="block text-sm font-semibold">Cash on delivery</span><span className="block text-xs text-ink-500">Pay the delivery person.</span></span>
          </button>
        )}
      </section>

      <section className="rounded-xl bg-white p-4 text-sm">
        {lines.map((l) => (
          <div key={l.product.id} className="flex justify-between py-0.5"><span>{l.product.name} {l.product.pack_label} × {l.qty}</span><span className="tabular-nums">{rupees(l.total)}</span></div>
        ))}
        <div className="flex justify-between py-0.5 text-ink-500"><span>Delivery</span><span>{fee ? rupees(fee) : 'Free'}</span></div>
        <div className="mt-1 flex justify-between border-t border-dashed border-ink-300/50 pt-2 text-base font-bold"><span>Total</span><span className="text-brand-green-800">{rupees(total)}</span></div>
        <p className="mt-2 text-xs text-ink-500">{front.order_note}</p>
      </section>

      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" className="mt-0.5 h-5 w-5 accent-brand-green-700" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
        <span>My details are correct. I understand my order is confirmed once the farm receives the payment.{errors.agree && <span className="block text-xs font-medium text-critical-600">{errors.agree}</span>}</span>
      </label>
      {/* honeypot - hidden from people */}
      <input className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" value={hp} onChange={(e) => setHp(e.target.value)} name="website" />

      {error && <p className="rounded-lg bg-critical-50 p-3 text-sm text-critical-700" role="alert">{error}</p>}
      <button type="submit" disabled={busy || short > 0 || !front.open} className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-green-700 py-3.5 text-base font-bold text-white disabled:bg-ink-300">
        <Lock className="h-4 w-4" /> {busy ? 'Placing order…' : pay === 'UPI' ? `Place order & pay ${rupees(total)}` : `Place order · ${rupees(total)}`}
      </button>
      <p className="text-center text-xs text-ink-400">Items {rupees(subtotal)} — prices are checked again when you place the order.</p>
    </form>
  );
}
